import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { StorageKeys, strongPasswordRegex } from '@core/constants';
import { JWT_INSTANCE } from '@core/helpers/jwt';
import { DecodedToken } from '@core/models/decoded-token';
import { UserModel } from '@core/models/user-model';
import { AlertService } from '@core/services/alert/alert.service';
import { Login } from '@core/state/auth/auth.actions';
import {
  FinishUserFirstPassword,
  InitUserFirstPassword,
  ValidateResetPasswordToken,
} from '@core/state/invitation/invitation.actions';
import {
  selectInvitationLoading,
  selectInvitationTokenValidity,
  selectRequestInProgress,
} from '@core/state/invitation/invitation.selectors';
import { PasswordValidators } from '@core/validators/password-validators';
import { Store } from '@ngrx/store';
import {
  FormValidationMessageModule,
  PasswordRulesModule,
} from '@shipmercury/mercury-ui';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { ButtonDirective, ButtonLabel } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { PasswordModule } from 'primeng/password';
import { Observable } from 'rxjs';
import { PublicBaseComponent } from 'src/app/public-base.component';

import { ResetPasswordService } from './reset-password.service';

@Component({
  selector: 'au-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  imports: [
    ReactiveFormsModule,
    PasswordModule,
    DividerModule,
    PasswordRulesModule,
    FormValidationMessageModule,
    ButtonDirective,
    RouterLink,
    AsyncPipe,
    RecaptchaModule,
    ButtonLabel,
  ],
})
export class ResetPasswordComponent
  extends PublicBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;
  @ViewChild('turnstileRef') turnstileRef?: ElementRef<HTMLDivElement>;

  jwt = inject(JWT_INSTANCE);
  form!: FormGroup;
  token: string = '';
  mercuryLogoPath = '../../assets/images/logo-original.svg';
  isInvitationModelValid$: Observable<boolean>;
  isTokenValidationLoading$: Observable<boolean>;
  isRequestInProgress$: Observable<boolean>;
  isTokenValid: boolean = false;
  strongRegex = strongPasswordRegex;

  constructor(
    fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private resetPasswordService: ResetPasswordService,
    private alertService: AlertService,
    private store: Store<any>
  ) {
    super();
    this.storageService.delete(StorageKeys.authUser);
    this.storageService.delete(StorageKeys.captchaResponse);
    this.form = fb.group(
      {
        password: [
          null,
          {
            validators: [
              Validators.required,
              PasswordValidators.containsUpperCaseCharacter,
              PasswordValidators.containsLowerCaseCharacter,
              PasswordValidators.containsSpeicalCharacter,
              PasswordValidators.containsNumericCharacter,
              Validators.minLength(8),
            ],
          },
        ],
        passwordRepeat: [
          null,
          {
            validators: [
              Validators.required,
              PasswordValidators.containsUpperCaseCharacter,
              PasswordValidators.containsLowerCaseCharacter,
              PasswordValidators.containsSpeicalCharacter,
              PasswordValidators.containsNumericCharacter,
              Validators.minLength(8),
            ],
          },
        ],
      },
      {
        validators: [PasswordValidators.passwordsMatch()],
        updateOn: 'change',
      }
    );

    this.isInvitationModelValid$ = store.select(selectInvitationTokenValidity);
    this.isTokenValidationLoading$ = store.select(selectInvitationLoading);
    this.isRequestInProgress$ = store.select(selectRequestInProgress);
  }

  get password(): FormControl {
    return this.form.get('password') as FormControl;
  }

  get passwordRepeat(): FormControl {
    return this.form.get('passwordRepeat') as FormControl;
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.params['token'];
    this.resetPasswordService.validateToken(this.token).subscribe((res) => {
      if (res && res.success && res.data) {
        this.isTokenValid = res.data.valid === true;
      }
      this.store.dispatch(
        ValidateResetPasswordToken({ isTokenValid: this.isTokenValid })
      );
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.enableRecaptchaFlag) {
      return;
    }

    if (this.useTurnstile) {
      await this.initializeTurnstile(this.turnstileRef!, (token: string) =>
        this.resolved(token)
      );
    }
  }

  submitRequestWithCaptcha(): void {
    this.captchaRef.execute();
  }

  resolved(captchaResponse: any) {
    if (captchaResponse) {
      this.storageService.save(captchaResponse, StorageKeys.captchaResponse);
      if (!this.useTurnstile) {
        this.sendResetPasswordRequest();
        this.captchaRef.reset();
      } else {
        this.verifiedSuccessfully = true;
      }
    }
  }

  async sendResetPasswordRequest() {
    if (this.turnstileWidgetId) {
      this.verifiedSuccessfully = false;
      window.turnstile?.reset(this.turnstileWidgetId);
    }
    this.store.dispatch(InitUserFirstPassword({ model: {} }));

    this.resetPasswordService
      .sendResetRequest(
        this.token,
        this.password.value,
        this.passwordRepeat.value
      )
      .subscribe((res) => {
        if (res && res.success && res.data) {
          this.saveAccessToken(res.data);

          this.router.navigateByUrl('/login');
        } else if (res && !res.success && res.errorMessage) {
          this.alertService.error({ message: res.errorMessage });
        }
        this.store.dispatch(FinishUserFirstPassword({ model: {} }));
      });
  }

  private saveAccessToken(data: any) {
    let decodedToken = this.jwt.decodeToken<DecodedToken>(data.accessToken);
    if (!decodedToken) return;

    let user: UserModel = {
      name: decodedToken.name,
      email: decodedToken.email,
      customerId: decodedToken.customerId,
      authToken: data.accessToken,
      refreshToken: data.refreshToken,
      userId: decodedToken.userId,
    };
    this.store.dispatch(Login({ user }));

    return user;
  }

  ngOnDestroy(): void {
    if (this.turnstileWidgetId) {
      window.turnstile?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }
}
